import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import {
  User,
  Edit3,
  MapPin,
  CreditCard,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Trash2,
  Camera,
  KeyRound,
} from "lucide-react";
import { toast } from "react-toastify";
import ZipCodeModal from "../ZipCodeModal";
import type { SettingsTabProps } from "./types";
import {
  checkScheduledCancellation,
  type ScheduledCancellationResponse,
} from "../../services/BillingService";
import { deleteAvatar, patchUser, uploadAvatar, type User as DealHawkUser } from "../../services/UserService";

export default function ProfileTab({ user, t, setActiveTab }: SettingsTabProps) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [showZipCodeModal, setShowZipCodeModal] = useState(false);
  const [savingPhoto, setSavingPhoto] = useState(false);
  const [photoFailed, setPhotoFailed] = useState(false);
  const [editingName, setEditingName] = useState(false);
  const [nameDraft, setNameDraft] = useState("");
  const [savingName, setSavingName] = useState(false);
  const [scheduledCancellation, setScheduledCancellation] =
    useState<ScheduledCancellationResponse | null>(null);
  const [isLoadingSubscription, setIsLoadingSubscription] = useState(true);

  const applyUser = (next: DealHawkUser) => {
    queryClient.setQueryData<DealHawkUser>(["user"], (old) => (old ? { ...old, ...next, avatarUrl: next.avatarUrl } : next));
    setPhotoFailed(false);
    void queryClient.invalidateQueries({ queryKey: ["community-successes"] });
  };

  const initials = (name?: string) =>
    (name || "DH").split(/\s+/).map((word) => word[0]).join("").slice(0, 2).toUpperCase();

  const onPickPhoto = async (file?: File) => {
    if (!file || savingPhoto) return;
    if (file.size > 2 * 1024 * 1024) {
      toast.error(t("products.photoTooLarge"));
      return;
    }
    setSavingPhoto(true);
    try {
      applyUser(await uploadAvatar(file));
      toast.success(t("products.photoUpdated"));
    } catch (error) {
      toast.error(error instanceof Error ? error.message : t("products.photoUpdateError"));
    } finally {
      setSavingPhoto(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const onRemovePhoto = async () => {
    if (savingPhoto) return;
    setSavingPhoto(true);
    try {
      applyUser(await deleteAvatar());
      toast.success(t("products.photoRemoved"));
    } catch (error) {
      toast.error(error instanceof Error ? error.message : t("products.photoUpdateError"));
    } finally {
      setSavingPhoto(false);
    }
  };

  const startEditingName = () => {
    setNameDraft(user?.name || "");
    setEditingName(true);
  };

  const onSaveName = async () => {
    const name = nameDraft.trim().slice(0, 120);
    if (savingName) return;
    if (!name || name === user?.name) {
      setEditingName(false);
      return;
    }
    setSavingName(true);
    try {
      applyUser(await patchUser({ name }));
      setEditingName(false);
      toast.success(t("products.nameUpdated"));
    } catch (error) {
      toast.error(error instanceof Error ? error.message : t("products.nameUpdateError"));
    } finally {
      setSavingName(false);
    }
  };

  useEffect(() => {
    const loadSubscriptionStatus = async () => {
      try {
        setIsLoadingSubscription(true);
        const cancellation = await checkScheduledCancellation();
        setScheduledCancellation(cancellation);
      } catch (error) {
        console.error("Error loading subscription status:", error);
      } finally {
        setIsLoadingSubscription(false);
      }
    };

    loadSubscriptionStatus();
  }, []);

  const handleZipCodeSuccess = () => {
    setShowZipCodeModal(false);
  };

  const hasActiveSubscription = user?.retailers && user.retailers.length > 0;

  const formatDate = (timestamp: number | null) => {
    if (!timestamp) return "N/A";
    return new Date(timestamp * 1000).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  return (
    <div className="space-y-3">
      {/* Subscription Status - Compact */}
      <div className="bg-gray-50 dark:bg-dark-700 rounded-lg border border-gray-200 dark:border-dark-600 p-4">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <CreditCard className="h-4 w-4 text-gray-600 dark:text-dark-300" />
            <h4 className="text-sm font-medium text-gray-900 dark:text-white">{t("products.subscriptionLabel")}</h4>
          </div>
          {setActiveTab && (
            <button
              onPointerDown={() => setActiveTab("billing")}
              className="text-xs text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 font-medium"
            >
              {t("products.manageArrow")}
            </button>
          )}
        </div>

        {isLoadingSubscription ? (
          <div className="flex items-center justify-center py-4">
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary-600"></div>
            <span className="ml-2 text-xs text-gray-600 dark:text-dark-300">{t("common.loading")}</span>
          </div>
        ) : (
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              {hasActiveSubscription ? (
                scheduledCancellation?.hasScheduledCancellation ? (
                  <AlertTriangle className="w-4 h-4 text-orange-600 dark:text-orange-400 flex-shrink-0" />
                ) : (
                  <CheckCircle className="w-4 h-4 text-green-600 dark:text-green-400 flex-shrink-0" />
                )
              ) : (
                <XCircle className="w-4 h-4 text-gray-400 dark:text-dark-500 flex-shrink-0" />
              )}
              <span
                className={`text-sm font-medium ${
                  hasActiveSubscription
                    ? scheduledCancellation?.hasScheduledCancellation
                      ? "text-orange-900 dark:text-orange-300"
                      : "text-green-900 dark:text-green-300"
                    : "text-gray-900 dark:text-white"
                }`}
              >
                {hasActiveSubscription
                  ? scheduledCancellation?.hasScheduledCancellation
                    ? t("products.cancellingAtPeriodEnd")
                    : t("products.activeLabel")
                  : t("products.noSubscription")}
              </span>
            </div>

            {hasActiveSubscription && (
              <div className="pt-2 border-t border-gray-200 dark:border-dark-600 space-y-1.5">
                {user.retailers && user.retailers.length > 0 && (
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-gray-600 dark:text-dark-400">{t("products.plansLabel")}</span>
                    <span className="text-gray-900 dark:text-white font-medium">
                      {user.retailers
                        .map((r: string) =>
                          r
                            .split("_")
                            .map(
                              (word: string) =>
                                word.charAt(0).toUpperCase() + word.slice(1)
                            )
                            .join(" ")
                        )
                        .join(", ")}
                    </span>
                  </div>
                )}

                {scheduledCancellation?.hasScheduledCancellation &&
                  scheduledCancellation.currentPeriodEnd && (
                    <div className="flex items-center justify-between text-xs">
                      <span className="text-gray-600 dark:text-dark-400">{t("products.accessEnds")}</span>
                      <span className="text-gray-900 dark:text-white">
                        {formatDate(scheduledCancellation.currentPeriodEnd)}
                      </span>
                    </div>
                  )}

                {!scheduledCancellation?.hasScheduledCancellation && (
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-gray-600 dark:text-dark-400">{t("products.statusLabel")}</span>
                    <span className="text-gray-900 dark:text-white">{t("products.renewsMonthly")}</span>
                  </div>
                )}
              </div>
            )}

            {!hasActiveSubscription && (
              <p className="text-xs text-gray-600 dark:text-dark-400 pt-2 border-t border-gray-200 dark:border-dark-600">
                {t("products.startSubscriptionHint")}
              </p>
            )}
          </div>
        )}
      </div>

      {/* Profile Info - Compact Grid */}
      <div className="bg-gray-50 dark:bg-dark-700 rounded-lg border border-gray-200 dark:border-dark-600 p-4">
        <div className="flex items-center gap-2 mb-3">
          <User className="h-4 w-4 text-gray-600 dark:text-dark-300" />
          <h4 className="text-sm font-medium text-gray-900 dark:text-white">{t("products.profileLabel")}</h4>
        </div>
        <div className="mb-3 flex items-center gap-3">
          <input ref={fileInputRef} type="file" accept="image/jpeg,image/png,image/gif" className="sr-only" onChange={(event) => void onPickPhoto(event.target.files?.[0])} />
          <button type="button" onClick={() => fileInputRef.current?.click()} disabled={savingPhoto} className="relative h-16 w-16 shrink-0 overflow-hidden rounded-full bg-violet-600 text-sm font-extrabold text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-600 disabled:opacity-60" aria-label={t("products.changePhoto")}>
            {user?.avatarUrl && !photoFailed ? <img src={user.avatarUrl} alt="" className="h-full w-full object-cover" onError={() => setPhotoFailed(true)} /> : initials(user?.name)}
            <span className="absolute inset-x-0 bottom-0 flex items-center justify-center bg-black/55 py-1"><Camera size={12} /></span>
          </button>
          <div className="min-w-0">
            <p className="truncate text-sm font-semibold text-gray-900 dark:text-white">{user?.name || t("products.notProvided")}</p>
            <div className="mt-1 flex flex-wrap gap-2">
              <button type="button" onClick={() => fileInputRef.current?.click()} disabled={savingPhoto} className="text-xs font-medium text-primary-600 hover:text-primary-700 dark:text-primary-400">{savingPhoto ? t("common.loading") : t("products.changePhoto")}</button>
              {user?.avatarUrl && <button type="button" onClick={() => void onRemovePhoto()} disabled={savingPhoto} className="text-xs font-medium text-gray-500 hover:text-rose-600 dark:text-dark-300">{t("products.removePhoto")}</button>}
            </div>
          </div>
        </div>
        <div className="space-y-2">
          {editingName ? (
            <form
              className="flex items-center gap-2 text-xs"
              onSubmit={(event) => {
                event.preventDefault();
                void onSaveName();
              }}
            >
              <span className="text-gray-600 dark:text-dark-400">{t("products.nameLabel")}</span>
              <input
                autoFocus
                value={nameDraft}
                maxLength={120}
                onChange={(event) => setNameDraft(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === "Escape") setEditingName(false);
                }}
                placeholder={t("products.namePlaceholder")}
                aria-label={t("products.nameLabel")}
                className="min-w-0 flex-1 rounded-md border border-gray-300 bg-white px-2 py-1 text-xs text-gray-900 focus:border-primary-500 focus:outline-none dark:border-dark-500 dark:bg-dark-800 dark:text-white"
              />
              <button type="submit" disabled={savingName} className="font-medium text-primary-600 hover:text-primary-700 disabled:opacity-60 dark:text-primary-400">
                {savingName ? t("common.loading") : t("common.save")}
              </button>
              <button type="button" onClick={() => setEditingName(false)} disabled={savingName} className="font-medium text-gray-500 hover:text-gray-700 dark:text-dark-300">
                {t("common.cancel")}
              </button>
            </form>
          ) : (
            <button
              type="button"
              onClick={startEditingName}
              aria-label={t("products.editName")}
              className="group flex w-full items-center justify-between gap-2 text-left text-xs"
            >
              <span className="text-gray-600 dark:text-dark-400">{t("products.nameLabel")}</span>
              <span className="flex min-w-0 items-center gap-1.5 font-medium text-gray-900 dark:text-white">
                <span className="truncate">{user?.name || t("products.notProvided")}</span>
                <Edit3 className="h-3.5 w-3.5 flex-shrink-0 text-gray-400 group-hover:text-primary-600 dark:text-dark-400 dark:group-hover:text-primary-400" />
              </span>
            </button>
          )}
          <div className="h-px bg-gray-200 dark:bg-dark-600"></div>
          <div className="flex items-center justify-between text-xs">
            <span className="text-gray-600 dark:text-dark-400">{t("products.emailLabel")}</span>
            <span className="text-gray-900 dark:text-white font-medium break-all text-right max-w-[60%]">
              {user?.userId || t("products.notProvided")}
            </span>
          </div>
        </div>
      </div>

      {/* Location - Compact */}
      <div className="bg-gray-50 dark:bg-dark-700 rounded-lg border border-gray-200 dark:border-dark-600 p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 flex-1 min-w-0">
            <MapPin className="h-4 w-4 text-gray-600 dark:text-dark-300 flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-1">
                {t("products.zipCodeSetting")}
              </h4>
              <p className="text-xs text-gray-600 dark:text-dark-400">
                {user?.zipCode ? user.zipCode : "33442"}
              </p>
            </div>
          </div>
          <button
            onPointerDown={() => setShowZipCodeModal(true)}
            className="p-2 text-gray-400 dark:text-dark-400 hover:text-primary-600 dark:hover:text-primary-400 hover:bg-primary-50 dark:hover:bg-primary-900/30 rounded-lg transition-colors flex-shrink-0"
            style={{ touchAction: "manipulation" }}
          >
            <Edit3 className="h-4 w-4" />
          </button>
        </div>
      </div>

      {/* Change Password */}
      <button
        onPointerDown={() => navigate("/change-password")}
        className="w-full flex items-center justify-between bg-gray-50 dark:bg-dark-700 rounded-lg border border-gray-200 dark:border-dark-600 p-4 text-left hover:bg-gray-100 dark:hover:bg-dark-600 transition-colors"
        style={{ touchAction: "manipulation" }}
      >
        <div className="flex items-center gap-2">
          <KeyRound className="h-4 w-4 text-gray-600 dark:text-dark-300" />
          <h4 className="text-sm font-medium text-gray-900 dark:text-white">Change password</h4>
        </div>
        <Edit3 className="h-4 w-4 text-gray-400 dark:text-dark-400" />
      </button>

      {/* Delete Account */}
      <button
        onPointerDown={() => navigate("/delete-account")}
        className="w-full flex items-center gap-2 text-xs text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg p-3 transition-colors"
        style={{ touchAction: "manipulation" }}
      >
        <Trash2 className="h-4 w-4" />
        <span className="font-medium">{t("products.deleteAccount")}</span>
      </button>

      {/* Zip Code Modal */}
      <ZipCodeModal
        isOpen={showZipCodeModal}
        onClose={() => setShowZipCodeModal(false)}
        onSuccess={handleZipCodeSuccess}
        currentZipCode={user?.zipCode || ""}
        title={t("products.updateZipCode")}
        description={t("products.updateZipCodeDesc")}
      />
    </div>
  );
}
